/// <amd-module name="VSSUI/Navigation/VssHubViewState" />
import { IFilter, IFilterState, FilterOperatorType } from "VSSUI/Utilities/Filter";
import { IObservableValue } from "VSS/Core/Observable";
import { IHubViewState, IHubViewStateOptions, HubViewOptionKeys, HubViewStateEventNames } from "../Utilities/HubViewState";
import { IViewStateChangeEvent, ViewStateFilter, ViewStateObservableValue, ViewStateOptions } from "../Utilities/ViewState";
import { IViewOptions, IViewOptionsValues, VIEW_OPTIONS_CHANGE_EVENT } from "../Utilities/ViewOptions";
import { IVssPageContext } from "VSS/Platform/Context";
import { INavigationParameter, INavigationParameters, HistoryBehavior } from "./ViewStateNavigation";
import { IVssViewState, IVssNavigationParameter, VssViewState } from "./VssViewState";

/**
 * View state for hubs which integrates with the navigation history service
 */
/** @deprecated */
export interface IVssHubViewState extends IHubViewState, IVssViewState<IViewOptionsValues | IFilterState | string> {}

/**
 * Options for creating a VssHubViewState
 */
/** @deprecated */
export interface IVssHubViewStateOptions extends IHubViewStateOptions {
    /**
     * Customizes the navigation parameter name used for the selected pivot. "view" is used by default (if undefined).
     * Can be set to null or empty string to indicate that the pivot should not be used as a navigation parameter.
     */
    pivotNavigationParamName?: string;

    /**
     * Indicates whether to add a new history entry when the pivot is changed or just replace/update the existing entry.
     * If not specified, the default behavior is to add a new entry on pivot changes.
     */
    pivotNavigationBehavior?: HistoryBehavior;

    /**
     * Describes the set of view option values that should be persisted to the URL
     */
    viewOptionNavigationParameters?: IVssNavigationParameter[];

    /**
     * Describes the set of filter items that should be persisted to the URL
     */
    filterNavigationParameters?: IVssNavigationParameter[];
}

/**
 * Default name of the navigation parameter for the selected pivot (can be overridden in NavigationHubViewState options)
 */
/** @deprecated */
export const DEFAULT_PIVOT_PARAM_NAME = "view";

let globalViewOptionParams: IVssNavigationParameter[] = [
    { key: HubViewOptionKeys.showFilterBar, serialize: value => (value ? "true" : undefined) },
    { key: HubViewOptionKeys.fullScreen, serialize: value => (value ? "true" : undefined) }
];

/**
 * Base view state for VSS hubs
 */
/** @deprecated */
export class VssHubViewState extends VssViewState<IViewOptionsValues | IFilterState | string> implements IVssHubViewState {
    private _viewOptions: IViewOptions;
    private _filter: IFilter;
    private _selectedPivot: IObservableValue<string>;
    private _pivotParamName: string;

    private viewOptionParams: { [key: string]: IVssNavigationParameter };
    private filterParams: { [key: string]: IVssNavigationParameter };
    private pivotNavigationBehavior: HistoryBehavior;
    private chromeless: boolean;

    constructor(pageContext: IVssPageContext, options: IVssHubViewStateOptions = {}, setupNavigation: boolean = true) {
        super(pageContext);

        this._viewOptions = new ViewStateOptions(this.notify.bind(this) as any, HubViewStateEventNames.viewOptionsChanging, options.viewOptions);
        this._filter = new ViewStateFilter(this.notify.bind(this) as any, HubViewStateEventNames.filterChanging, options.filterOptions);

        this.viewOptionParams = {};
        this.chromeless = document.body.classList.contains("chromeless");

        // Add global view option nav parameters
        for (let param of globalViewOptionParams) {
            this.viewOptionParams[param.key] = param;
        }

        // Add view option params from options
        if (options.viewOptionNavigationParameters) {
            for (let param of options.viewOptionNavigationParameters) {
                if (param.behavior === HistoryBehavior.none) {
                    delete this.viewOptionParams[param.key];
                } else {
                    this.viewOptionParams[param.key] = param;
                }
            }
        }

        this.filterParams = {};

        // Add filter params from options
        if (options.filterNavigationParameters) {
            for (let param of options.filterNavigationParameters) {
                this.filterParams[param.key] = param;
            }
        }

        // Create the selected-pivot observable
        this.pivotNavigationBehavior = options.pivotNavigationBehavior === undefined ? HistoryBehavior.newEntry : options.pivotNavigationBehavior;
        this._pivotParamName = options.pivotNavigationParamName === undefined ? DEFAULT_PIVOT_PARAM_NAME : options.pivotNavigationParamName;
        if (this._pivotParamName) {
            this._selectedPivot = this.createNavParamObservableValue(
                HubViewStateEventNames.pivotChanging,
                this._pivotParamName,
                true,
                this.pivotNavigationBehavior,
                options.defaultPivot,
                options.defaultPivot
            );
        } else {
            this._selectedPivot = new ViewStateObservableValue<string>(
                this.notify.bind(this) as any,
                HubViewStateEventNames.pivotChanging,
                options.defaultPivot
            );
        }

        if (setupNavigation) {
            this.setupNavigation();
        }

        this.viewOptions.subscribe(this._onViewOptionsChanged, VIEW_OPTIONS_CHANGE_EVENT);
    }

    public get selectedPivot(): IObservableValue<string> {
        return this._selectedPivot;
    }

    public get filter(): IFilter {
        return this._filter;
    }

    public get viewOptions(): IViewOptions {
        return this._viewOptions;
    }

    public canSwitchPivots(targetPivot: string): boolean {
        return true;
    }

    protected setupNavigation() {
        super.setupNavigation();
        this._setFullScreenMode(this.viewOptions.getViewOption(HubViewOptionKeys.fullScreen) === true);
    }

    public dispose() {
        super.dispose();
        this.viewOptions.unsubscribe(this._onViewOptionsChanged, VIEW_OPTIONS_CHANGE_EVENT);
    }

    protected getNavigationParameters(): INavigationParameters {
        const navParameters = super.getNavigationParameters();

        // Add navigation params for view option parameters
        for (let key in this.viewOptionParams) {
            const param = this.viewOptionParams[key];
            navParameters[param.navParamName || param.key] = this.createViewOptionNavParam(param);
        }

        // Add navigation params for filter parameters
        for (let key in this.filterParams) {
            const param = this.filterParams[key];
            const paramName = param.navParamName || param.key;

            // Add 2 entries for filters: One for the value, and another for the operator
            navParameters[paramName] = this.createFilterNavValueParam(param);
            navParameters["op." + paramName] = this.createFilterNavOperatorParam(param);
        }

        return navParameters;
    }

    private createViewOptionNavParam(param: IVssNavigationParameter): INavigationParameter {
        return {
            getValue: () => {
                return this.getStringValueForNavParam(this.viewOptions.getViewOption(param.key), param);
            },
            setValue: (newValueString: string) => {
                const existingValue = this.viewOptions.getViewOption(param.key);
                this.updateFromNavParamString(existingValue, newValueString, param, value => {
                    this.viewOptions.setViewOption(param.key, value);
                });
            }
        };
    }

    private createFilterNavValueParam(param: IVssNavigationParameter): INavigationParameter {
        return {
            getValue: () => {
                return this.getStringValueForNavParam(this.filter.getFilterItemValue(param.key), param);
            },
            setValue: (newValueString: string) => {
                const existingValue = this.filter.getFilterItemValue(param.key);
                this.updateFromNavParamString(existingValue, newValueString, param, value => {
                    const existingState = this.filter.getFilterItemState(param.key);
                    this.filter.setFilterItemState(param.key, { value: value, operator: existingState && existingState.operator });
                });
            }
        };
    }

    private createFilterNavOperatorParam(param: IVssNavigationParameter): INavigationParameter {
        return {
            getValue: () => {
                const state = this.filter.getFilterItemState(param.key);
                return state && state.operator === FilterOperatorType.or ? "or" : undefined;
            },
            setValue: (newValue: string) => {
                const existingState = this.filter.getFilterItemState(param.key);
                if (newValue === "or") {
                    if (!existingState || existingState.operator !== FilterOperatorType.or) {
                        this.filter.setFilterItemState(param.key, { value: existingState && existingState.value, operator: FilterOperatorType.or });
                    }
                } else {
                    if (existingState && existingState.operator === FilterOperatorType.or) {
                        this.filter.setFilterItemState(param.key, { value: existingState.value, operator: FilterOperatorType.and });
                    }
                }
            }
        };
    }

    private _onViewOptionsChanged = (changedOptions: IViewOptionsValues) => {
        // Handle full-screen mode changes
        if (HubViewOptionKeys.fullScreen in changedOptions) {
            this._setFullScreenMode(changedOptions[HubViewOptionKeys.fullScreen] === true);
        }
    };

    private _setFullScreenMode(fullScreenMode: boolean) {
        if (fullScreenMode || this.chromeless) {
            document.body && document.body.classList.add("full-screen-mode");
        } else {
            document.body && document.body.classList.remove("full-screen-mode");
        }
    }

    protected getUpdateBehavior(e: IViewStateChangeEvent<IViewOptionsValues | IFilterState | string>): HistoryBehavior {
        let behavior = super.getUpdateBehavior(e);

        switch (e.eventName) {
            case HubViewStateEventNames.pivotChanging:
                behavior = this.pivotNavigationBehavior;
                break;

            case HubViewStateEventNames.viewOptionsChanging:
                for (let optionKey in e.eventValue as IViewOptionsValues) {
                    const param = this.viewOptionParams[optionKey];
                    if (param && param.behavior !== HistoryBehavior.none) {
                        behavior = param.behavior;
                        if (param.behavior === HistoryBehavior.replace) {
                            break;
                        }
                    }
                }
                break;

            case HubViewStateEventNames.filterChanging:
                for (let optionKey in e.eventValue as IFilterState) {
                    const param = this.filterParams[optionKey];
                    if (param && param.behavior !== HistoryBehavior.none) {
                        behavior = param.behavior;
                        if (param.behavior === HistoryBehavior.replace) {
                            break;
                        }
                    }
                }
                break;
        }

        return behavior;
    }
}
